(function () {
  let isAdding = false;
  let rewardQueue = Promise.resolve();

  function asNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    return sections;
  }

  async function getCart() {
    const response = await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'));
    if (!response.ok) throw new Error('Unable to load cart. Please try again.');
    return response.json();
  }

  async function fetchCartMutation(url, body) {
    const response = await fetch(url, {
      ...theme.utils.fetchConfig('json'),
      body: JSON.stringify(body),
    });

    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || 'Unable to update your gift. Please try again.');
    return cart;
  }

  function publishCartUpdate(source, cart, sections) {
    cart.sections = sections;
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source,
      cart,
    });
  }

  function getConfig() {
    const element = document.querySelector('.olgc-milestone-rewards[data-mystery-gift-variant-id]');
    if (!element) return null;

    const mysteryGiftVariantId = asNumber(element.dataset.mysteryGiftVariantId);
    const mysteryGiftThreshold = asNumber(element.dataset.mysteryGiftThreshold);
    const premiumGiftVariantId = asNumber(element.dataset.premiumGiftVariantId);
    const premiumGiftThreshold = asNumber(element.dataset.premiumGiftThreshold);
    const giftWrapVariantId = asNumber(element.dataset.giftWrapVariantId);

    if (!mysteryGiftVariantId || !mysteryGiftThreshold) return null;

    return {
      mysteryGiftVariantId,
      mysteryGiftThreshold,
      premiumGiftVariantId,
      premiumGiftThreshold,
      giftWrapVariantId,
    };
  }

  function isRewardLine(item, config) {
    return hasRewardProperty(item) || item.variant_id === config.mysteryGiftVariantId || item.variant_id === config.premiumGiftVariantId;
  }

  function hasRewardProperty(item) {
    const properties = item.properties || {};

    if (Array.isArray(properties)) {
      return properties.some(([name, value]) => name === '_olgc_milestone_reward' && value);
    }

    return Boolean(properties._olgc_milestone_reward);
  }

  function getEligibleSubtotal(cart, config) {
    // Use the same post-discount subtotal as the Liquid reward progress.
    // Legacy paid reward lines are removed before this calculation.
    return Math.max(0, cart.total_price - cart.items.reduce((total, item) => (
      item.variant_id === config.giftWrapVariantId ? total + item.final_line_price : total
    ), 0));
  }

  function getEligibleRewardLabel(eligibleSubtotal, config) {
    if (config.premiumGiftVariantId && config.premiumGiftThreshold && eligibleSubtotal >= config.premiumGiftThreshold) {
      return 'Premium Gift';
    }

    if (eligibleSubtotal >= config.mysteryGiftThreshold) {
      return 'Mystery Gift';
    }

    return '';
  }

  function enqueueRewardUpdate(operation) {
    const pending = rewardQueue.then(operation);
    rewardQueue = pending.catch(() => {});
    return pending;
  }

  function canonicalReward(label) {
    if (label === 'Premium Gift worth \u20b91,999') return 'Premium Gift';
    return label || '';
  }

  async function updateRewardCart(body) {
    return fetchCartMutation(theme.routes.cart_update_url, {
      ...body,
      sections: getBundledSections(),
      sections_url: window.location.pathname,
    });
  }

  async function normalizeRewardCart() {
    const config = getConfig();
    let cart = await getCart();
    if (!config) return { cart, config };

    let selectedReward = canonicalReward(cart.attributes?.['Milestone Reward']);
    const rewardLines = cart.items.filter((item) => isRewardLine(item, config));
    if (rewardLines.length) {
      // Preserve the existing checkout attribute workflow, but remove the paid
      // variant before Shopify calculates coupons, rather than at checkout.
      if (!selectedReward) {
        const selectedLine = rewardLines.find((item) => item.variant_id === config.premiumGiftVariantId)
          || rewardLines.find((item) => item.variant_id === config.mysteryGiftVariantId);
        if (selectedLine) {
          selectedReward = selectedLine.variant_id === config.premiumGiftVariantId ? 'Premium Gift' : 'Mystery Gift';
        }
      }
      cart = await updateRewardCart({
        updates: Object.fromEntries(rewardLines.map((item) => [item.key, 0])),
        attributes: { 'Milestone Reward': selectedReward },
      });
    }

    const eligibleReward = getEligibleRewardLabel(getEligibleSubtotal(cart, config), config);
    if (selectedReward !== eligibleReward) selectedReward = '';
    if ((cart.attributes?.['Milestone Reward'] || '') !== selectedReward) {
      cart = await updateRewardCart({ attributes: { 'Milestone Reward': selectedReward } });
    }
    if (cart.sections) publishCartUpdate('olgc-milestone-rewards-cleanup', cart, cart.sections);
    return { cart, config };
  }

  function showRewardError(button, error) {
    console.error(error);
    const container = button?.closest('.olgc-milestone-rewards');
    if (!container) return;
    let message = container.querySelector('[data-olgc-reward-error]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.olgcRewardError = '';
      message.setAttribute('role', 'alert');
      container.append(message);
    }
    message.textContent = error.message || 'Unable to update your gift. Please try again.';
  }

  async function addReward(event) {
    const button = event.target.closest('[data-olgc-add-reward], [data-olgc-remove-reward]');
    if (!button || isAdding || !window.theme?.routes || !window.theme?.pubsub) return;

    isAdding = true;
    button.disabled = true;
    button.classList.add('is-adding');
    button.closest('.olgc-milestone-rewards')?.querySelector('[data-olgc-reward-error]')?.remove();

    try {
      await enqueueRewardUpdate(async () => {
        const { cart, config } = await normalizeRewardCart();
        if (!config) throw new Error('Please refresh your cart and try again.');
        const removing = button.hasAttribute('data-olgc-remove-reward');
        const label = removing ? '' : button.dataset.rewardLabel;
        const eligibleReward = getEligibleRewardLabel(getEligibleSubtotal(cart, config), config);
        const expectedVariant = label === 'Premium Gift' ? config.premiumGiftVariantId : config.mysteryGiftVariantId;
        if (!removing && (label !== eligibleReward || asNumber(button.dataset.rewardVariantId) !== expectedVariant)) {
          throw new Error('Your cart has changed. Please check the available gift.');
        }
        const updatedCart = await updateRewardCart({ attributes: { 'Milestone Reward': label } });
        publishCartUpdate('olgc-milestone-rewards', updatedCart, updatedCart.sections);
      });
    }
    catch (error) {
      showRewardError(button, error);
    }
    finally {
      button.disabled = false;
      button.classList.remove('is-adding');
      isAdding = false;
    }
  }

  async function removeUnqualifiedRewards() {
    if (!window.theme?.routes || !window.theme?.pubsub || !getConfig()) return;
    try {
      await enqueueRewardUpdate(normalizeRewardCart);
    }
    catch (error) {
      console.error(error);
    }
  }

  // Checkout waits for any in-flight gift selection and rechecks eligibility.
  window.olgcMilestoneRewards = {
    prepareCart: () => enqueueRewardUpdate(normalizeRewardCart),
  };

  function scheduleRewardCleanup() {
    window.requestAnimationFrame(removeUnqualifiedRewards);
  }

  document.addEventListener('click', addReward);
  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source === 'olgc-milestone-rewards-cleanup') return;
    scheduleRewardCleanup();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRewardCleanup);
  }
  else {
    scheduleRewardCleanup();
  }
})();
